import type { SurfStyle } from "@vida/surf-core";

const MAX_SWATCHES = 6;

function palettesFromArrays(bg: string[], tx: string[]): SurfStyle["palette"] {
  return {
    surface: bg[0] ?? "#f8fafc",
    text: tx[0] ?? "#0f172a",
    accent: tx[1] ?? bg[1] ?? "#38bdf8",
    muted: bg[2] ?? tx[2] ?? "#94a3b8",
  };
}

function normalizeHex(raw: string): string | null {
  const h = raw.trim();
  return /^#[0-9a-fA-F]{6}$/.test(h) ? h.toLowerCase() : null;
}

type Props = {
  backgroundColors: string[];
  textColors: string[];
  onBackgroundChange: (colors: string[]) => void;
  onTextChange: (colors: string[]) => void;
};

export function PaletteEditor({
  backgroundColors,
  textColors,
  onBackgroundChange,
  onTextChange,
}: Props) {
  const patchBg = (index: number, hex: string) => {
    const next = [...backgroundColors];
    next[index] = hex;
    onBackgroundChange(next);
  };

  const patchTx = (index: number, hex: string) => {
    const next = [...textColors];
    next[index] = hex;
    onTextChange(next);
  };

  const removeBg = (index: number) => {
    onBackgroundChange(backgroundColors.filter((_, i) => i !== index));
  };

  const removeTx = (index: number) => {
    onTextChange(textColors.filter((_, i) => i !== index));
  };

  const addBg = () => {
    if (backgroundColors.length >= MAX_SWATCHES) return;
    onBackgroundChange([...backgroundColors, "#334155"]);
  };

  const addTx = () => {
    if (textColors.length >= MAX_SWATCHES) return;
    onTextChange([...textColors, "#e2e8f0"]);
  };

  return (
    <div className="palette-editor">
      <SwatchGrid
        title="Palette colori"
        colors={backgroundColors}
        onChange={patchBg}
        onRemove={removeBg}
        onAdd={addBg}
        canAdd={backgroundColors.length < MAX_SWATCHES}
      />
      <SwatchGrid
        title="Accenti e testi"
        colors={textColors}
        onChange={patchTx}
        onRemove={removeTx}
        onAdd={addTx}
        canAdd={textColors.length < MAX_SWATCHES}
      />
    </div>
  );
}

function SwatchGrid({
  title,
  colors,
  onChange,
  onRemove,
  onAdd,
  canAdd,
}: {
  title: string;
  colors: string[];
  onChange: (index: number, hex: string) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
  canAdd: boolean;
}) {
  return (
    <div className="palette-swatch-group">
      <h4>{title}</h4>
      <div className="palette-swatch-grid" role="list">
        {colors.map((hex, i) => (
          <div key={`${title}-${i}`} className="palette-swatch-row" role="listitem">
            <label className="palette-swatch-label">
              <input
                type="color"
                value={hex}
                aria-label={`${title} colore ${i + 1}`}
                onChange={(e) => onChange(i, e.target.value)}
              />
              <span className="palette-swatch-preview" style={{ background: hex }} aria-hidden />
            </label>
            <input
              type="text"
              className="palette-hex-input"
              value={hex}
              aria-label={`${title} hex ${i + 1}`}
              onChange={(e) => {
                const n = normalizeHex(e.target.value);
                if (n) onChange(i, n);
              }}
            />
            <button
              type="button"
              className="surf-icon-btn"
              aria-label={`Rimuovi colore ${i + 1}`}
              disabled={colors.length <= 1}
              onClick={() => onRemove(i)}
            >
              −
            </button>
          </div>
        ))}
      </div>
      {canAdd && (
        <button type="button" className="surf-btn surf-btn--ghost palette-add-btn" onClick={onAdd}>
          + Aggiungi colore
        </button>
      )}
    </div>
  );
}

export function derivePaletteFromStyle(style: SurfStyle): {
  bg: string[];
  tx: string[];
} {
  const bg =
    style.paletteBackground?.length
      ? [...style.paletteBackground]
      : [style.palette.surface, style.palette.muted, style.palette.accent].filter(Boolean);
  const tx =
    style.paletteText?.length
      ? [...style.paletteText]
      : [style.palette.text, style.palette.accent, style.palette.muted].filter(Boolean);
  return { bg: bg.slice(0, MAX_SWATCHES), tx: tx.slice(0, MAX_SWATCHES) };
}

export function applyPaletteArrays(
  style: SurfStyle,
  bg: string[],
  tx: string[],
): SurfStyle {
  return {
    ...style,
    paletteBackground: bg,
    paletteText: tx,
    palette: palettesFromArrays(bg, tx),
  };
}
